import React from 'react';
import {Dialog, DialogContent, DialogTitle} from '@mui/material';
import {AxiosInstance} from 'axios';
import {useTranslation} from 'react-i18next';
import Asset from '../../../models/Asset';
import {AiPromptVariant} from '../../../models/Template';
import AiMagicStage from './AiMagicStage';

interface AiMagicDialogProps {
    open: boolean;
    api: AxiosInstance;
    templateId: string;
    inputKey: string;
    designKey?: string;
    sourceAsset?: Asset;
    sourcePreviewUrl?: string;
    autoResumeJobId?: string;
    /** Named style options for the field. >1 shows the picker. */
    aiPromptVariants?: AiPromptVariant[];
    /** Field frame aspect — enables the crop step (with folderId). */
    frameSize?: { width: number; height: number };
    folderId?: string;
    /** Field removes background: the result is a padded cutout, no crop step. */
    removeBackground?: boolean;
    onAccept: (asset: Asset) => void;
    onClose: () => void;
}

/** Standalone host for the AI stage.
 *
 *  Two callers: Fill Mode's RESUME path (no upload dialog to advance — the user
 *  reopened a field whose job is still running or already finished), and the
 *  desktop picker's "AI edit this photo" action on an asset already in the
 *  library. The resume path passes no variants/frame; the picker passes the
 *  field's full config so the experience matches the post-upload one. */
const AiMagicDialog: React.FC<AiMagicDialogProps> = ({
                                                         open,
                                                         api,
                                                         templateId,
                                                         inputKey,
                                                         designKey,
                                                         sourceAsset,
                                                         sourcePreviewUrl,
                                                         autoResumeJobId,
                                                         aiPromptVariants,
                                                         frameSize,
                                                         folderId,
                                                         removeBackground,
                                                         onAccept,
                                                         onClose,
                                                     }) => {
    const {t} = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                    background: 'linear-gradient(220deg, #051726 36%, #025989 100%)',
                    color: 'white',
                },
            }}
        >
            <DialogTitle sx={{pb: 1, fontSize: '1.05rem', fontWeight: 700}}>
                {t('aiEdit.title')}
            </DialogTitle>
            <DialogContent sx={{pb: 3}}>
                <AiMagicStage
                    api={api}
                    templateId={templateId}
                    inputKey={inputKey}
                    designKey={designKey}
                    sourceAsset={sourceAsset}
                    sourcePreviewUrl={sourcePreviewUrl}
                    autoResumeJobId={autoResumeJobId}
                    aiPromptVariants={aiPromptVariants}
                    frameSize={frameSize}
                    folderId={folderId}
                    removeBackground={removeBackground}
                    onAccept={onAccept}
                    onKeepOriginal={onClose}
                />
            </DialogContent>
        </Dialog>
    );
};

export default AiMagicDialog;
